import { lazy, Suspense, useCallback, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { SlideOut } from '~/chords/SlideOut';

import type { LabelMappingFlatten } from '../../api/label-mapping';
import { getLabelMappingFlatten } from '../../api/label-mapping';
import type { DataLoader } from '../../components/DataGrid/types';
import { PageLoader } from '../../components/PageLoader';
import { useAmplifyUser } from '../../context/useAmplifyUser';
import { getColumnDefs } from './columns';
import { LabelForm } from './components/LabelForm';
import { LabelMappingCtxProvider } from './context/Provider';

const LazyLabelMappingGrid = lazy(() =>
  import('../../components/DataGrid').then((module) => ({
    default: module.DataGrid,
  })),
);

function LabelMapping() {
  const { t } = useTranslation('label-perf');
  const { user } = useAmplifyUser();
  const [search, setSearch] = useState<string | undefined>('');
  const [selectedLabel, setSelectLabel] = useState<
    LabelMappingFlatten | undefined
  >(undefined);
  const [reloadHash, setReloadHash] = useState(new Date().toISOString());

  const dataLoader: DataLoader<Partial<LabelMappingFlatten>> =
    useCallback(async () => {
      let data = await getLabelMappingFlatten();

      data = data.filter((l) => {
        if (!search) {
          return true;
        }

        const searchLower = search.toLowerCase();

        if (searchLower === 'n/a' || searchLower === 'empty') {
          return l.mapped_labels.length === 0;
        }

        return (
          l.name.toLowerCase().includes(searchLower) ||
          l.parent_path.toLowerCase().includes(searchLower) ||
          l.mapped_labels.some((m) =>
            m.mapped_label.toLowerCase().includes(searchLower),
          )
        );
      });

      return {
        data,
        total: data.length,
      };
    }, [search]);

  const onClose = useCallback(() => {
    setSelectLabel(undefined);
    setReloadHash(new Date().toISOString());
  }, []);

  return (
    <div className="flex h-[calc(100%-126px)] flex-wrap p-xl">
      <Helmet>
        <title>
          {t('header.title')} - {t('title.labelMapping')}
        </title>
      </Helmet>
      {user ? (
        <Suspense fallback={<PageLoader />}>
          <LabelMappingCtxProvider
            value={{ selectedLabel, setSelectedLabel: setSelectLabel }}
          >
            <LazyLabelMappingGrid
              columnDefs={getColumnDefs(
                ['admin', 'supervisor'].includes(user.role),
              )}
              dataLoader={dataLoader}
              limit={1000}
              onSearch={setSearch}
              reloadHash={reloadHash}
              views={[]}
              title="labelMapping"
              searchPlaceholder="Search label"
            />

            <SlideOut
              open={!!selectedLabel}
              onOpenChange={() => setSelectLabel(undefined)}
              panelWidth="560px"
            >
              <LabelForm label={selectedLabel!} closeHandler={onClose} />
            </SlideOut>
          </LabelMappingCtxProvider>
        </Suspense>
      ) : (
        <PageLoader />
      )}
    </div>
  );
}

LabelMapping.displayName = 'LabelMapping';

export { LabelMapping };
